import {
  BlazeDataComplexityResponse,
  DataPublishConfig,
  DataSettings,
  DataSourceIteration,
  IterationOverride,
  IterationOverrides,
} from 'types/rest/blazeDataTypes';
import { IterationOptions, MeManipulatorDM } from '@blazemeter/blaze-data-designer-client';
import { DataModel, DataSource, DSType, Entity, getEntitiesFromDataModel } from '@blazemeter/blaze-data-model';
import _ from 'lodash';
import { MeModel } from '@blazemeter/blaze-data-model/src/model/DataModel';
import { PropertyGroupFromDataModel } from '@blazemeter/blaze-data-ui';
import { dataDesignerFetch } from '../axios/tdm-http-client';
import blazeDataClient from '../blazeData/blazeDataClient';

//Default repeat count when not available in TDM
const ITERATION_REPEAT_COUNT = -1;

/**
 * Creates fallback iteration config for entity when no config is available (either from TDM or from virtual services)
 * @param entityKey
 */
export const getFallbackPublishConfig = (entityKey: string) => {
  return {
    entityKey: entityKey,
    iterationOptions: IterationOptions.DEFINED,
    repeat: ITERATION_REPEAT_COUNT,
  };
};

/**
 * Builds data model wrapper with config overrides.
 * @param dataAttributes
 */
export const getPublishConfig = (dataAttributes: IterationOverrides): DataSettings => {
  return { data: { type: 'datamodel', attributes: { iterationOverride: { entities: dataAttributes } } } };
};

/**
 * Gets model complexity information from blaze data backend.
 * @param workspaceId
 * @param modelId
 */
export const getModelComplexity = async (
  workspaceId: number,
  modelId: string,
  override?: any,
): Promise<BlazeDataComplexityResponse> => {
  return (await dataDesignerFetch('POST', `/api/v1/workspaces/${workspaceId}/datamodels/${modelId}/size`, override))
    .body;
};

/**
 * Checks if the count is above threshold.
 *
 * @param count
 */
export const isComplexityOverThreshold = (count: number) => {
  return count >= 100000;
};

/**
 * Returns true if account limit has been applied in one of the listed data entities.
 * @param complexity
 */
export const isBlazeDataLimitApplied = (complexity: BlazeDataComplexityResponse) => {
  if (_.isNil(complexity) || !_.has(complexity, 'billingLimits')) {
    return false;
  }
  const entities = _.values(complexity.billingLimits.entity);
  return _.some(entities, entity => entity.applied);
};

/**
 * Gets entity from model by name.
 * @param entityName
 */
export const getEntityFromModel = (entityName: string, dataModel: DataModel | null): Entity | null => {
  if (_.isNil(dataModel)) {
    return null;
  }

  const entities = (dataModel as MeModel).entities;
  return entities && entities[entityName] ? entities[entityName] : null;
};

/**
 * Creates publish config ui representation based on iteration overrides.
 * @param key - entity key
 * @param iterationOverride - iteration overrides
 */
export const createPublishConfig = (key: string, iterationOverride: IterationOverride): DataPublishConfig | null => {
  const baseConfig = {
    entityKey: key,
    repeat: iterationOverride.repeat && iterationOverride.repeat > 0 ? iterationOverride.repeat : 1, //Default repeat set to 1
  };

  //Fixed number of iterations or no data sources
  if (iterationOverride.repeat !== -1 || !(iterationOverride.datasources && iterationOverride.datasources.length > 0)) {
    return {
      ...baseConfig,
      iterationOptions: IterationOptions.DEFINED,
    };
  }

  // Only one data set is available, use is as a single specific resource
  if (iterationOverride.datasources.length === 1) {
    return {
      ...baseConfig,
      iterationOptions: IterationOptions.ALL_FROM_DS,
      resourceName: iterationOverride.datasources[0].name,
    };
  }

  const numberOfLoops = iterationOverride.datasources.filter(item => item.loop === true).length;
  const isAllCsvLoop = numberOfLoops === iterationOverride.datasources.length;

  // For min / max compare the number of loops in datasets
  if (isAllCsvLoop || numberOfLoops === 0) {
    return {
      ...baseConfig,
      iterationOptions: isAllCsvLoop ? IterationOptions.MAX_ROWS : IterationOptions.MIN_ROWS,
    };
  }
  // For specific resource, find the one with loop = false
  else {
    const specificResource = _.find(iterationOverride.datasources, item => item.loop === false);
    if (specificResource) {
      return {
        ...baseConfig,
        iterationOptions: IterationOptions.ALL_FROM_DS,
        resourceName: specificResource.name,
      };
    }
  }

  return null;
};

/**
 * Extracts iteration overrides from TDM entity.
 * @param entity - entity from TDM
 */
export const getPublishConfigFromTdmEntity = (entity: Entity | null): IterationOverride => {
  // Reasonable default if entity is not available
  if (_.isNil(entity)) {
    return {
      repeat: ITERATION_REPEAT_COUNT,
      datasources: [],
    };
  }

  // Read data sources and its settings
  const dataSourcesIterationOptions: DataSourceIteration[] = [];
  if (!_.isNil(entity.datasources)) {
    entity.datasources.forEach(ds => {
      dataSourcesIterationOptions.push({
        loop: ds.loop,
        name: ds.name,
      });
    });
  }

  return {
    repeat: getRepeatCountFromTdmEntity(entity),
    datasources: dataSourcesIterationOptions,
  };
};

export const getRepeatCountFromTdmEntity = (entity: Entity) => {
  return _.get(entity, 'repeat', ITERATION_REPEAT_COUNT);
};

export const isModelDifferent = (modelA: any, modelB: any) => {
  if (_.isNil(modelA) && _.isNil(modelB)) {
    return false;
  }
  return !_.isEqual(modelA, modelB);
};

export const getPreviewTitleSuffix = (generatedDataCount: number, total?: number) => {
  return total && total > generatedDataCount
    ? `Showing Only First 100 of Total ${total} Iterations`
    : generatedDataCount;
};

/**
 * Gets a property group from a model
 * @param dataModel
 * @param entityName
 */
export const extractPropertyGroupsFromManipulatorModel = (dataModel: MeManipulatorDM, entityName: string) => {
  const pgs: Array<PropertyGroupFromDataModel> = [];
  try {
    for (const name in dataModel.entities[entityName].propertiesGroups) {
      if (dataModel.entities[entityName].propertiesGroups.hasOwnProperty(name)) {
        const pg = dataModel.entities[entityName].propertiesGroups[name];
        const properties = Object.keys(pg.properties);
        if (properties.length > 0) {
          pgs.push({ name, displayName: pg.displayName, groupType: pg.groupType, properties });
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return pgs;
};

/**
 * Transforms UI DataPublishConfig to iteration overrides.
 * @param publishConfigs - collection of ui config objects
 * @param createDataSourceConfig - function which creates data source loop configuration
 */
export const getIterationOverrides = (
  publishConfigs: DataPublishConfig[],
  createDataSourceConfig: any,
): IterationOverrides => {
  const iterationOverride: IterationOverrides = {};
  _.forEach(publishConfigs, (config: DataPublishConfig) => {
    // If there is a fixed repeat number set, use it
    let repeat = -1;
    if (config.iterationOptions === IterationOptions.DEFINED) {
      repeat = config.repeat;
    }

    iterationOverride[config.entityKey] = {
      repeat: repeat,
      datasources: createDataSourceConfig(config),
    };
  });

  return iterationOverride;
};

export const createDsConfig = (config: DataPublishConfig, entity: Entity | null) => {
  if (_.isNil(entity) || _.isNil(entity.datasources)) {
    return;
  }
  const dataSources = entity.datasources.map((ds: DataSource<DSType>) => {
    const dsConfig: any = {
      name: ds.name,
      loop: false,
    };

    switch (config.iterationOptions) {
      case IterationOptions.DEFINED:
        return { ...dsConfig, loop: true };
      case IterationOptions.ALL_FROM_DS:
        //We will loop other csvs but the one user is interested in
        return { ...dsConfig, loop: config.resourceName !== ds.name };
      case IterationOptions.MAX_ROWS:
        return { ...dsConfig, loop: true };
      case IterationOptions.MIN_ROWS:
        return { ...dsConfig, loop: false };
    }

    return dsConfig;
  });

  return dataSources;
};

/**
 * Generates user friendly file name from download link from shared folder.
 */
export const getFileNameFromDownloadLink = (downloadLink: string) => {
  const fallbackName = 'download.json';
  const fileName = downloadLink.split('/').pop();
  if (fileName) {
    const splitByExtension = fileName.split('.json');
    return splitByExtension.length >= 2 ? splitByExtension[0] + '.json' : fallbackName;
  } else {
    return fallbackName;
  }
};

/**
 * Merges model overrides stored in virtual service with blaze data entities.
 * @param tdmEntities
 * @param overrides
 */
export const mergeOverrideWithDataModel = (
  tdmEntities: { [entityName: string]: Entity },
  overrides: IterationOverrides,
) => {
  const mergedModel: any = _.merge(tdmEntities, overrides);
  _.forEach(overrides, (override: IterationOverride, key: string) => {
    // If any entity is set to have fixed number of rows we need to set data source loop to true
    if (override.repeat && override.repeat !== -1) {
      if (!_.isNil(mergedModel[key])) {
        _.forEach(mergedModel[key].datasources, ds => {
          ds.loop = true;
        });
      }
    }
  });
  return mergedModel;
};

/**
 * Generates preview for a property in the data model.
 * @param dataModel
 * @param entityName
 * @param propertyName
 */
export const generatePreviewForProperty = async (
  dataModel: MeModel,
  entityName: string,
  propertyName: string,
): Promise<string> => {
  const manipulator = blazeDataClient.manipulator;
  const generatedResult = await manipulator.generator.generateStoredModel(
    dataModel.id,
    dataModel,
    propertyName,
    1,
    entityName,
  );
  return _.get(generatedResult, `${entityName}.data[0].${propertyName}`) as any;
};
/**
 * Converts data model structure to structure for tree table.
 * @param dataModel
 */
export const convertDataModelToTree = (dataModel?: DataModel) => {
  if (_.isNil(dataModel)) {
    throw new Error('No data model');
  }

  const treeData: any[] = [];
  const dmEntities = getEntitiesFromDataModel(dataModel);
  _.forOwn(dmEntities, (entity, key) => {
    const nodeItem = {
      name: entity.title,
      id: key,
      type: 'NODE',
      leafText: 'Text',
      parentId: null,
    };
    treeData.push(nodeItem);
    _.forEach(entity.requirements, (requirement, reqKey) => {
      const leafItem = {
        name: reqKey,
        id: reqKey,
        type: 'LEAF',
        leafText: requirement,
        parentId: key,
      };
      treeData.push(leafItem);
    });
  });

  return treeData;
};

/**
 * Gets number of entities in the model.
 * @param dataModel
 */
export const countEntitiesInDataModel = (dataModel: DataModel | null) => {
  if (_.isNil(dataModel) || _.isNil((dataModel as MeModel).entities)) {
    return 0;
  }
  return _.values((dataModel as MeModel).entities).length;
};
